export type ScheduleType = 'one_time' | 'daily' | 'weekly' | 'monthly' | 'custom';
export type DayOfWeek = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

export interface ListSchedule {
  id: string;
  listId: string;
  type: ScheduleType;
  
  // For one-time schedules
  scheduledDate?: Date;
  scheduledTime?: string; // HH:MM format
  
  // For recurring schedules
  recurringPattern?: RecurringPattern;
  
  // Schedule metadata
  title?: string; // Optional custom title for the schedule
  description?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  
  // Notification settings
  notifications?: ScheduleNotification[];
}

export interface RecurringPattern {
  // Daily
  dailyInterval?: number; // Every X days (1 = daily, 2 = every other day)
  
  // Weekly
  weeklyInterval?: number; // Every X weeks
  daysOfWeek?: DayOfWeek[]; // Which days of the week
  
  // Monthly
  monthlyInterval?: number; // Every X months
  dayOfMonth?: number; // Specific day of month (1-31)
  weekOfMonth?: number; // Which week of the month (1-4, -1 for last)
  dayOfWeekInMonth?: DayOfWeek; // Which day of the week in that week
  
  // Custom pattern
  customDates?: Date[]; // Specific dates for custom patterns
  
  // Pattern constraints
  startDate: Date;
  endDate?: Date; // Optional end date
  maxOccurrences?: number; // Stop after X occurrences
  
  // Time settings
  time: string; // HH:MM format
  timezone?: string; // Timezone identifier
}

export interface ScheduleNotification {
  id: string;
  type: 'email' | 'push' | 'in_app';
  minutesBefore: number; // How many minutes before scheduled time
  message?: string;
  isEnabled: boolean;
}

export interface CalendarEvent {
  id: string;
  listId: string;
  scheduleId: string;
  title: string;
  description?: string;
  startTime: Date;
  endTime?: Date;
  isAllDay: boolean;
  
  // List information
  listTitle: string;
  listStatus: string;
  listProgress: number;
  goalId?: string;
  goalTitle?: string;
  aspirationId?: string;
  aspirationTitle?: string;
  
  // Event metadata
  isRecurring: boolean;
  recurringInstanceId?: string; // For specific instances of recurring events
  color?: string;
  priority: number;
}

export interface CalendarViewOptions {
  view: 'week' | 'month' | 'day';
  startDate: Date;
  showCompletedLists: boolean;
  showTemplates: boolean;
  filterByGoal?: string[];
  filterByAspiration?: string[];
  priorityThreshold?: number; // Only show lists with priority >= this value
}

export interface TodaysPriority {
  id: string;
  listId: string;
  title: string;
  description?: string;
  priority: number;
  progress: number;
  totalItems: number;
  completedItems: number;
  scheduledTime?: string;
  isOverdue: boolean;
  goalTitle?: string;
  aspirationTitle?: string;
  estimatedMinutes?: number;
}

export interface WeekViewData {
  weekStart: Date;
  weekEnd: Date;
  days: DayViewData[];
}

export interface DayViewData {
  date: Date;
  isToday: boolean;
  isWeekend: boolean;
  events: CalendarEvent[];
  priorities: TodaysPriority[];
  totalScheduledMinutes: number;
  availableMinutes: number; // Based on working hours
}

export interface CalendarStats {
  totalScheduledLists: number;
  completedThisWeek: number;
  overdueCount: number;
  upcomingDeadlines: {
    today: number;
    thisWeek: number;
    nextWeek: number;
  };
  averageCompletionRate: number; // Percentage
  mostProductiveDay: DayOfWeek;
}

// Working hours configuration
export interface WorkingHours {
  monday: TimeSlot;
  tuesday: TimeSlot;
  wednesday: TimeSlot;
  thursday: TimeSlot;
  friday: TimeSlot;
  saturday: TimeSlot;
  sunday: TimeSlot;
}

export interface TimeSlot {
  isWorkingDay: boolean;
  startTime: string; // HH:MM
  endTime: string; // HH:MM
  breakTimes?: { start: string; end: string; title?: string; }[];
}

// Default working hours (9 AM - 5 PM on weekdays)
export const DEFAULT_WORKING_HOURS: WorkingHours = {
  monday: { isWorkingDay: true, startTime: '09:00', endTime: '17:00' },
  tuesday: { isWorkingDay: true, startTime: '09:00', endTime: '17:00' },
  wednesday: { isWorkingDay: true, startTime: '09:00', endTime: '17:00' },
  thursday: { isWorkingDay: true, startTime: '09:00', endTime: '17:00' },
  friday: { isWorkingDay: true, startTime: '09:00', endTime: '17:00' },
  saturday: { isWorkingDay: false, startTime: '10:00', endTime: '14:00' },
  sunday: { isWorkingDay: false, startTime: '10:00', endTime: '14:00' }
};